import React, { useState } from 'react'
import { IconButton, Menu, MenuItem, Typography, Popover, Box, Chip } from '@mui/material';
import { Edit, Delete, KeyboardArrowRight, ArrowDropDown, InfoOutlined } from '@mui/icons-material';
import { InputAdornment, OutlinedInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, } from "@mui/material";
import { formatAmountForDisplayWithPreferences } from 'utils';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { ButtonType } from './invoiceButtonComponents';
import { getFrameDetails } from './glassesOrderMapper';
import { usePreferences } from 'hooks/usePreferences';
import { FRAME_HANDLING_PATIENT_OWN, FRAME_HANDLING_STOCK, FRAME_HANDLING_ORDER_NEW } from '../invoice';

const getLensTypeCode = (lensType) => {
    const typeMap = {
        single_vision: 'SV', progressive: 'P', bifocal: 'BF',
        trifocal: 'TF', aphakic: 'AP', vocational: 'VOC'
    };
    return typeMap[lensType] || 'LENS';
};

const getLensTypeColor = (lensType) => {
    const colorMap = {
        single_vision: 'success', progressive: 'primary', bifocal: 'secondary',
        trifocal: 'warning', aphakic: 'error', vocational: 'info'
    };
    return colorMap[lensType] || 'default';
};

/**
 * Extract and format contact lens display information for invoice items table
 * Reads from item associations (contactLensModel, contactLensSchedule) rather than itemConfig
 * @param {Object} item - Invoice item with contact lens data
 * @returns {Object} Display info with eye label, model, manufacturer, schedule
 */
const getContactLensDisplayInfo = (item) => {
    const eye = item.eye || '';
    const contactLensModel = item.contactLensModel || {};
    const contactLensSchedule = item.contactLensSchedule || {};
    const config = item.itemConfig || {};

    return {
        eyeLabel: eye ? `(${eye})` : '',
        modelName: contactLensModel.modelName || config.modelName || 'Contact Lens',
        manufacturerName: contactLensModel.manufacturer?.name || 'Unknown',
        scheduleName: contactLensSchedule.name || '',
        lensesPerBox: config.lensesPerBox || 0
    };
};

function InvoiceItemsTable({ invoiceItems, onItemUpdate, onDeleteItem, onEditItem }) {
    // Predefined rates
    const taxRates = [0, 5, 13, 15];
    const discountRates = [0, 5.00, 10, 15, 20, 25, 30, 40, 50];

    // Add state for managing menus
    const [taxAnchorEl, setTaxAnchorEl] = useState(null);
    const [discountAnchorEl, setDiscountAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [frameInfoAnchor, setFrameInfoAnchor] = useState(null);
    const [frameInfoItem, setFrameInfoItem] = useState(null);
    const { preferences } = usePreferences();

    // Menu handlers
    const handleTaxClick = (event, item) => {
        setTaxAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleDiscountClick = (event, item) => {
        setDiscountAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleTaxClose = () => {
        setTaxAnchorEl(null);
        setSelectedItem(null);
    };

    const handleDiscountClose = () => {
        setDiscountAnchorEl(null);
        setSelectedItem(null);
    };

    const handleTaxSelect = (rate) => {
        onItemUpdate(selectedItem, 'tax', rate);
        handleTaxClose();
    };

    const handleDiscountSelect = (rate) => {
        onItemUpdate(selectedItem, 'discount', rate);
        handleDiscountClose();
    };

    const handleFrameInfoClick = (event, item) => {
        setFrameInfoAnchor(event.currentTarget);
        const frameDetails = getFrameDetails(item);

        const normalized = {
            description: frameDetails?.description || item.description || '',
            brand: frameDetails?.brand || item.brand || null,
            modelNumber: frameDetails?.modelNumber || item.modelNumber || null,
            properties: {
                ...(frameDetails?.properties || {})
            },
            _originalItem: item
        };

        setFrameInfoItem(normalized);
    };

    const handleFrameInfoClose = () => {
        setFrameInfoAnchor(null);
        setFrameInfoItem(null);
    };

    const getFrameSizeString = (properties) => {
        const sizeParts = [];
        if (properties.aMeasurement) sizeParts.push(`${properties.aMeasurement}`);
        if (properties.bMeasurement) sizeParts.push(`${properties.bMeasurement}`);
        if (properties.dblMeasurement) sizeParts.push(`${properties.dblMeasurement}`);
        return sizeParts.join(' • ');
    }

    // Group items by groupId, handle individual items separately
    const { groupedItems, individualItems } = invoiceItems.reduce((acc, item) => {
        if (item.groupId) {
            if (!acc.groupedItems[item.groupId]) {
                acc.groupedItems[item.groupId] = [];
            }
            acc.groupedItems[item.groupId].push(item);
        } else {
            acc.individualItems.push(item);
        }
        return acc;
    }, { groupedItems: {}, individualItems: [] });

    const renderTableRow = (item, index, isGrouped = false, isFirstInGroup = false) => {
        const frameDetails = item.type === ButtonType.FRAME ? getFrameDetails(item) : null;

        return (
            <TableRow
                key={item.id || index}
                sx={{
                    backgroundColor: isGrouped ? 'action.hover' : 'inherit',
                    opacity: item._markedForDeletion ? 0.4 : 1,
                    textDecoration: item._markedForDeletion ? 'line-through' : 'none',
                    '& > td:first-of-type': {
                        paddingLeft: (isGrouped && !isFirstInGroup) ? 3 : 1
                    }
                }}
            >
                <TableCell sx={{ textTransform: 'capitalize' }}>
                    {item._markedForDeletion && (
                        <Typography
                            component="span"
                            sx={{
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                mr: 1,
                                px: 0.5,
                                py: 0.25,
                                backgroundColor: '#d32f2f',
                                borderRadius: 0.5,
                                opacity: 1
                            }}
                        >
                            MARKED FOR DELETION
                        </Typography>
                    )}
                    {isFirstInGroup && (
                        <KeyboardArrowRight
                            fontSize="small"
                            sx={{
                                verticalAlign: 'middle',
                                marginRight: 1
                            }}
                        />
                    )}
                    {item.type === ButtonType.LENS && item.itemConfig?.model?.lensType && (
                        <Chip
                            label={getLensTypeCode(item.itemConfig.model.lensType)}
                            size="small"
                            variant="outlined"
                            color={getLensTypeColor(item.itemConfig.model.lensType)}
                            sx={{ mr: 1, verticalAlign: 'middle', fontWeight: 700, fontSize: '0.7rem' }}
                        />
                    )}
                    {(item.type === 'contact_lens' || item.type === ButtonType.CONTACT_LENS) && (() => {
                        const clInfo = getContactLensDisplayInfo(item);
                        return (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    component="div"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {`Contact Lens ${clInfo.eyeLabel}: ${clInfo.modelName}`}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    sx={{ mt: 0.25 }}
                                >
                                    {item?.entryMethod === 'manual'
                                        ? (item?.description || '')
                                        : (
                                            <>
                                                {clInfo.manufacturerName}
                                                {clInfo.scheduleName && ` • ${clInfo.scheduleName}`}
                                                {clInfo.lensesPerBox && ` • ${clInfo.lensesPerBox} lenses/box`}
                                            </>
                                        )}
                                </Typography>
                            </Box>
                        );
                    })()}
                    {!(item.type === 'contact_lens' || item.type === ButtonType.CONTACT_LENS) && (
                        <Typography
                            variant="subtitle2"
                            component="span"
                            sx={{ verticalAlign: 'middle', fontWeight: 600, mr: 1 }}
                        >
                            {item.description}
                        </Typography>
                    )}
                    {item.type === ButtonType.FRAME && frameDetails?.properties && Object.keys(frameDetails.properties).length > 0 && (
                        <IconButton
                            size="small"
                            onClick={(e) => handleFrameInfoClick(e, item)}
                            sx={{ verticalAlign: 'middle', ml: 0.5 }}
                        >
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    )}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.25 }}
                    >
                        {frameDetails?.brand?.name && <span>{frameDetails.brand.name}</span>}
                        {frameDetails?.properties?.color && <span> • {frameDetails.properties.color}</span>}
                        {frameDetails?.properties && <span> • {getFrameSizeString(frameDetails.properties)}</span>}
                        {frameDetails?.properties?.frameType && <span> • {frameDetails.properties.frameType}</span>}
                        {frameDetails?.mode === FRAME_HANDLING_PATIENT_OWN && ' (POF)'}
                        {frameDetails?.mode === FRAME_HANDLING_STOCK && ' (Stock)'}
                        {frameDetails?.mode === FRAME_HANDLING_ORDER_NEW && ' (Order New)'}
                    </Typography>
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{item.type}</TableCell>
                <TableCell>{formatDateWithPreferences(item.date, preferences)}</TableCell>
                <TableCell align="left">
                    <TextField
                        size="small"
                        value={item.quantity}
                        onChange={(e) => onItemUpdate(item, 'quantity', e.target.value)}
                        inputProps={{
                            min: 0,
                            style: { textAlign: 'center' }
                        }}
                        sx={{ width: '6ch' }}
                    />
                </TableCell>
                <TableCell align="left">{formatAmountForDisplayWithPreferences(item.price, preferences)}</TableCell>
                <TableCell align="left">
                    <OutlinedInput
                        size="small"
                        value={item.discount}
                        onChange={(e) => onItemUpdate(item, 'discount', e.target.value)}
                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                        inputProps={{
                            min: 0,
                            style: { textAlign: 'left' }
                        }}
                        sx={{ width: '11ch' }}
                    />
                    <IconButton
                        size="small"
                        onClick={(e) => handleDiscountClick(e, item)}
                    >
                        <ArrowDropDown />
                    </IconButton>
                </TableCell>
                <TableCell align="left">
                    <OutlinedInput
                        size="small"
                        value={item.tax}
                        onChange={(e) => onItemUpdate(item, 'tax', e.target.value)}
                        endAdornment={<InputAdornment position="end">%</InputAdornment>}
                        inputProps={{
                            min: 0,
                            style: { textAlign: 'left' }
                        }}
                        sx={{ width: '11ch' }}
                    />
                    <IconButton
                        size="small"
                        onClick={(e) => handleTaxClick(e, item)}
                    >
                        <ArrowDropDown />
                    </IconButton>
                </TableCell>
                {/* <TableCell align="left">{formatAmountForDisplayWithPreferences(item.total, preferences)}</TableCell> */}
                <TableCell>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteItem(item)}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                    {(item.type === ButtonType.FRAME || item.type === ButtonType.LENS) && (
                        <IconButton
                            size="small"
                            onClick={() => onEditItem({ ...item, type: item.type })}
                            sx={{ mr: 1 }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    )}
                    {(item.type === 'contact_lens' || item.type === ButtonType.CONTACT_LENS) && (
                        <IconButton
                            size="small"
                            onClick={() => onEditItem({ ...item, type: ButtonType.CONTACT_LENS })}
                            sx={{ mr: 1 }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            <TableContainer sx={{ minHeight: 250, maxHeight: 400 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="left">Quantity</TableCell>
                            <TableCell align="left">Price</TableCell>
                            <TableCell align="left">Discount</TableCell>
                            <TableCell align="left">Tax</TableCell>
                            {/* removing total column becuase it creates confusion with the total in the summary section and it is not editable */}
                            {/* <TableCell align="left">Total</TableCell> */}
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            invoiceItems.length > 0 ? (
                                <>
                                    {/* Render grouped items */}
                                    {Object.entries(groupedItems).map(([groupId, items]) => (
                                        <React.Fragment key={groupId}>
                                            {items.map((item, index) =>
                                                renderTableRow(
                                                    item,
                                                    index,
                                                    true,  // isGrouped
                                                    index === 0  // isFirstInGroup
                                                )
                                            )}
                                        </React.Fragment>
                                    ))}

                                    {/* Render individual items */}
                                    {individualItems.map((item, index) =>
                                        renderTableRow(
                                            item,
                                            index,
                                            false,  // isGrouped
                                            false   // isFirstInGroup
                                        )
                                    )}
                                </>) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography>No items added to the invoice.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Tax Rate Menu */}
            <Menu
                anchorEl={taxAnchorEl}
                open={Boolean(taxAnchorEl)}
                onClose={handleTaxClose}
            >
                {taxRates.map((rate) => (
                    <MenuItem
                        key={rate}
                        onClick={() => handleTaxSelect(rate)}
                    >
                        {rate}%
                    </MenuItem>
                ))}
            </Menu>

            {/* Discount Rate Menu */}
            <Menu
                anchorEl={discountAnchorEl}
                open={Boolean(discountAnchorEl)}
                onClose={handleDiscountClose}
            >
                {discountRates.map((rate) => (
                    <MenuItem
                        key={rate}
                        onClick={() => handleDiscountSelect(rate)}
                    >
                        {rate}%
                    </MenuItem>
                ))}
            </Menu>

            {/* Frame Info Popover */}
            <Popover
                open={Boolean(frameInfoAnchor)}
                anchorEl={frameInfoAnchor}
                onClose={handleFrameInfoClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {frameInfoItem && (
                    <Box sx={{ p: 2, minWidth: 280, maxWidth: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            {frameInfoItem.description}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, mb: 1 }}>
                            {frameInfoItem.brand && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Brand:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.brand?.name}</Typography>
                                </>
                            )}
                            {frameInfoItem.modelNumber && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Model:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.modelNumber}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.color && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Color:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.color}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.size && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Size:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.size}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.material && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Material:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.material}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.shape && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Shape:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.shape}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.gender && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Gender:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.gender}</Typography>
                                </>
                            )}
                            {(frameInfoItem.properties?.a || frameInfoItem.properties?.b || frameInfoItem.properties?.dbl) && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Dimensions:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {[
                                            frameInfoItem.properties?.a && `A: ${frameInfoItem.properties.a}`,
                                            frameInfoItem.properties?.b && `B: ${frameInfoItem.properties.b}`,
                                            frameInfoItem.properties?.dbl && `DBL: ${frameInfoItem.properties.dbl}`
                                        ].filter(Boolean).join(' • ')}
                                    </Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.temple && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Temple:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.temple}</Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Popover>
        </>
    )
}

export default InvoiceItemsTable